import { useState, useEffect, useRef } from 'react';

const throttle = (callback: () => void, limit: number) => {
  let waiting = false;
  return () => {
    if (!waiting) {
      callback();
      waiting = true;
      setTimeout(() => {
        waiting = false;
      }, limit);
    }
  };
};

/**
 * @description This hook returns trigger, which is a boolean value that indicates the scroll direction of the target element.
 * @param {string} targetId - The id of the target element.
 * @param {number} options.scrollThrottle - The throttle time for the scroll event. Defaults to 300
 * @returns {boolean} trigger - Returns true if the user is scrolling down, otherwise false.
 */
export default function useScrollDirectionTrigger(
  targetId: string,
  options = { scrollThrottle: 300 }
) {
  const { scrollThrottle } = options;
  const [trigger, setTrigger] = useState(false);
  const previousScrollRef = useRef<any>(null);

  useEffect(() => {
    const targetEl = document.getElementById(targetId);
    if (!targetEl) return;

    const handleScroll = () => {
      const currentScrollY = targetEl.scrollTop;

      // If the user is scrolling down or at the top of the page, set the trigger to true
      if (currentScrollY > previousScrollRef.current || currentScrollY <= 1) {
        setTrigger(true);
      } else {
        setTrigger(false);
      }

      previousScrollRef.current = currentScrollY;
    };

    const throttledHandleScroll = throttle(handleScroll, scrollThrottle);
    targetEl.addEventListener('scroll', throttledHandleScroll);
    return () => {
      targetEl.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [targetId, scrollThrottle]);

  return {
    trigger,
  };
}
